import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { gallery } from '../data/content';
import { ResponsiveImage } from './ResponsiveImage';
import { GalleryMetadata } from './GalleryMetadata';

// Adapted from 21st.dev's Gallery Grid with Lightbox component (demo 10596).
// The native dialog replaces the source overlay to provide focus trapping and Escape behavior.

export function Gallery() {
  const [category, setCategory] = useState('Todos');
  const [selected, setSelected] = useState<number | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const reduced = useReducedMotion();
  const categories = ['Todos', ...new Set(gallery.map((item) => item.category))];
  const items = useMemo(() => category === 'Todos' ? gallery : gallery.filter((item) => item.category === category), [category]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (selected !== null && !dialog.open) dialog.showModal();
    if (selected === null && dialog.open) dialog.close();
  }, [selected]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (selected === null) return;
      if (event.key === 'ArrowRight') setSelected((selected + 1) % items.length);
      if (event.key === 'ArrowLeft') setSelected((selected - 1 + items.length) % items.length);
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [items.length, selected]);

  useEffect(() => {
    if (selected === null) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [selected]);

  const current = selected === null ? null : items[selected];
  const move = (direction: number) => selected !== null && setSelected((selected + direction + items.length) % items.length);

  return (
    <>
      <div className="gallery-filters" role="group" aria-label="Filtrar galeria por categoria">
        {categories.map((item) => <button key={item} type="button" aria-pressed={category === item} onClick={() => { setCategory(item); setSelected(null); }}>{item}</button>)}
      </div>
      <motion.div layout className="gallery-grid">
        <AnimatePresence mode="popLayout">
          {items.map((item, index) => (
            <motion.button
              layout
              key={item.id}
              type="button"
              className={`gallery-tile gallery-tile--${(index % 4) + 1}`}
              onClick={() => setSelected(index)}
              initial={reduced ? false : { opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduced ? undefined : { opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
            >
              <ResponsiveImage media={item.media} alt="" sizes="(max-width: 700px) 100vw, 50vw" />
              <span><strong>{item.media.caption || item.title}</strong><small>{item.category}</small><GalleryMetadata item={item} /></span>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>
      <dialog ref={dialogRef} className="lightbox" aria-label="Visualização de imagem" onClose={() => setSelected(null)} onClick={(event) => event.target === dialogRef.current && setSelected(null)}>
        {current && (
          <div className="lightbox__inner">
            <button className="lightbox__close" type="button" onClick={() => setSelected(null)} aria-label="Fechar imagem"><X /></button>
            <button className="lightbox__prev" type="button" onClick={() => move(-1)} aria-label="Imagem anterior"><ChevronLeft /></button>
            <ResponsiveImage media={current.media} sizes="95vw" priority />
            <button className="lightbox__next" type="button" onClick={() => move(1)} aria-label="Próxima imagem"><ChevronRight /></button>
            <div className="lightbox__caption"><strong>{current.media.caption || current.title}</strong><span>{current.category}</span><GalleryMetadata item={current} /></div>
          </div>
        )}
      </dialog>
    </>
  );
}
